import { connect } from 'react-redux';
import { push } from 'connected-react-router';

import PayOneTimePaymentPage from './component';
import { ErrorInterface } from '@common/types';
import { getRachelDashboard, requestPayOneTimePayment } from '@renter-hub/actions/requests';
import { editPaymentInfo } from '@renter-hub/actions/onboarding';
import { closeNotification, showPageNotification } from '@common/actions/NotificationActions';
import { checkRequestActionSuccess, getSignInPage } from '@common/paymentsUtils/api';
import {
  DEFAULT_ERROR,
  ONE_TIME_PAYMENT_PAID,
  PAYMENT_PLAN_INSTALLMENT_PAID,
} from '@renter-hub/constants/notifications';
import HTTP_STATUS from '@common/constants/HttpConstants';
import TransactionConstants from '@common/constants/TransactionConstants';
import paths from '@renter-hub/pathsManifest';
import ONE_TIME_PAYMENT_TYPE from '@common/constants/oneTimePaymentTypes';
import { RequestPayOneTimePaymentInterface } from '@renter-hub/models/RequestsTypes';
import { logError } from '@zg-rentals/log-error';
import { getVerifiedPaymentMethods } from '@common/paymentsUtils/multiplePaymentMethodsUtil';

const findOneTimePaymentTransaction = (decodedKey, transactions) => {
  const unpaidFailedRegex = `${TransactionConstants.STATUS.UNPAID}|${TransactionConstants.STATUS.PAYMENT_FAILED}`;
  return transactions
    .filter((transaction) => transaction.paymentCategory === TransactionConstants.CATEGORY.ONE_TIME_PAYMENT)
    .filter((transaction) =>
      transaction.paymentType === ONE_TIME_PAYMENT_TYPE.PAYMENT_PLAN
        ? String(transaction.requestKey) === decodedKey
        : String(transaction.groupKey) === decodedKey,
    )
    .filter((transaction) => transaction.renterStatus.match(new RegExp(unpaidFailedRegex)))
    .pop();
};

const mapStateToProps = (state, { otpKey, failedPaymentEmail }) => {
  const { transactions } = state.payments.renter.dashboard.paymentHistory;
  const { landlordProfile, paymentDetails } = state.payments.renter.dashboard;
  const decodedKey = decodeURI(otpKey);
  const transaction = findOneTimePaymentTransaction(decodedKey, transactions);
  const verifiedPaymentMethods = getVerifiedPaymentMethods(state.payments.renter.dashboard.paymentMethods);
  return {
    user: state.user,
    transaction,
    landlordName: landlordProfile.name,
    landlordFirstName: landlordProfile.firstName,
    accountLast4: paymentDetails.accountLast4,
    accountName: paymentDetails.accountName,
    failedPaymentEmail,
    paymentMethods: verifiedPaymentMethods,
    groupKey: decodedKey,
  };
};

const mapDispatchToProps = (dispatch, { baseUrl, propertyId }: any) => ({
  onSubmit: async (paymentInfo: RequestPayOneTimePaymentInterface, paymentPlanInstallmentTitle = '') => {
    try {
      // Close any open banners
      await dispatch(closeNotification());

      // Request to create one time payment
      const updatePaymentAccountResponse = await dispatch(requestPayOneTimePayment(paymentInfo));
      await checkRequestActionSuccess(updatePaymentAccountResponse);

      // Request the latest renter status
      const [renterStatusResponse] = await dispatch(getRachelDashboard({ propertyId }));
      await checkRequestActionSuccess(renterStatusResponse);

      // Navigate to dashboard
      await dispatch(push(baseUrl));

      // Show success notification
      if (paymentPlanInstallmentTitle) {
        await dispatch(showPageNotification(PAYMENT_PLAN_INSTALLMENT_PAID, { paymentPlanInstallmentTitle }));
      } else {
        await dispatch(showPageNotification(ONE_TIME_PAYMENT_PAID));
      }
      return { error: false };
    } catch (err) {
      const error = err as ErrorInterface;
      if (error.action && error.action.payload) {
        const { status, response } = error.action.payload;
        const { messageCode } = response || {};

        // If 401 status, redirect to sign-in
        if (status === HTTP_STATUS.SC_UNAUTHORIZED) {
          window.location = getSignInPage();
          return null;
        }

        if (status === HTTP_STATUS.SC_UNPROCESSABLE_ENTITY) {
          logError({ error: err, context: { status, messageCode } });
          dispatch(showPageNotification(DEFAULT_ERROR));
          return { error: true, failedPayment: true, paymentFailureReason: messageCode || '' };
        }
      }

      logError({ error: err });
      dispatch(showPageNotification(DEFAULT_ERROR));
      return { error: true };
    }
  },
  onEditPaymentInfo: async () => {
    await dispatch(closeNotification());
    await dispatch(editPaymentInfo());
    await dispatch(push(baseUrl + paths.editPaymentInfoPage, { fromWhere: paths.oneTimePaymentPage }));
  },
  goToDashboard: async () => {
    await dispatch(closeNotification());
    await dispatch(push(baseUrl));
  },
});

const mergeProps = (stateProps, dispatchProps) => ({
  ...stateProps,
  ...dispatchProps,
  onSubmit: (selectedPaymentMethodId: string) => {
    const { transaction } = stateProps;
    const { paymentType, requestKey, groupKey, paymentPlanInstallmentTitle } = transaction;

    if (paymentType === ONE_TIME_PAYMENT_TYPE.PAYMENT_PLAN) {
      return dispatchProps.onSubmit(
        {
          requestKey,
          encodedPaymentMethodId: selectedPaymentMethodId,
        },
        paymentPlanInstallmentTitle,
      );
    }
    return dispatchProps.onSubmit({ groupKey, encodedPaymentMethodId: selectedPaymentMethodId });
  },
});

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(PayOneTimePaymentPage);
